import type { MessageDeleteForUser, MessageRecalledDto } from '@/features/messages/models/message-action-dto';
import type { PinnedMessage } from '@/features/messages/models/message-pinned';
import type { MessageReactionsUpdateDto } from '@/features/messages/models/message-reaction';
import type { FriendOfflineStatusDto, FriendOnlineStatusDto } from '@/features/participant';
import { TypedEmitter } from '@/shared/utils/event-emitter';
import type { ConversationUpdateEvent } from './hooks/use-conversation-update-event';
import type { MessageReceiveEvent } from './hooks/use-message-receive-event';

/**
 * SignalR Hub Events
 * Defines all events emitted by the realtime hub connection
 */
export interface HubEvents extends Record<string, unknown> {
  // Connection events
  'connection:connecting': undefined;
  'connection:connected': undefined;
  'connection:reconnecting': Error | undefined;
  'connection:reconnected': undefined;
  'connection:disconnected': Error | undefined;
  'connection:error': Error;

  // Subscription events
  'subscription:subscribed': string;
  'subscription:unsubscribed': string;
  'subscription:resubscribed': string[];

  // Token events
  'token:expired': undefined;
  'token:refreshed': undefined;

  // Message events
  'message:received': MessageReceiveEvent;
  'message:recalled': MessageRecalledDto;
  'message:deletedForUser': MessageDeleteForUser;
  'message:pinned': PinnedMessage;
  'message:unpinned': PinnedMessage;
  'message:reactionsUpdated': MessageReactionsUpdateDto;
  'conversation:updated': ConversationUpdateEvent;
  'conversation:removed': string;
  'conversation:added': any;

  // Friend presence events
  'friend:online': FriendOnlineStatusDto;
  'friend:offline': FriendOfflineStatusDto;
}

/**
 * Hub Event Emitter
 * Type-safe event emitter for hub events
 */
export const hubEmitter = new TypedEmitter<HubEvents>();

/**
 * Event constants for better discoverability
 */
export const HUB_EVENTS = {
  CONNECTION: {
    CONNECTING: 'connection:connecting',
    CONNECTED: 'connection:connected',
    RECONNECTING: 'connection:reconnecting',
    RECONNECTED: 'connection:reconnected',
    DISCONNECTED: 'connection:disconnected',
    ERROR: 'connection:error'
  },
  SUBSCRIPTION: {
    SUBSCRIBED: 'subscription:subscribed',
    UNSUBSCRIBED: 'subscription:unsubscribed',
    RESUBSCRIBED: 'subscription:resubscribed'
  },
  TOKEN: {
    EXPIRED: 'token:expired',
    REFRESHED: 'token:refreshed'
  },
  MESSAGE: {
    RECEIVED: 'message:received',
    RECALLED: 'message:recalled',
    DELETED_FOR_USER: 'message:deletedForUser',
    PINNED: 'message:pinned',
    UNPINNED: 'message:unpinned',
    REACTIONS_UPDATED: 'message:reactionsUpdated',
    CONVERSATION_UPDATED: 'conversation:updated',
    CONVERSATION_REMOVED: 'conversation:removed',
    CONVERSATION_ADDED: 'conversation:added'
  }
} as const;

// Rely on centralized HMR manager via TypedEmitter registration; no per-module cleanup needed
// Keep dispose pattern available if code wants to explicitly dispose
// No runtime changes here; TypedEmitter registers itself automatically.

// Backwards compatibility: provide explicit dispose hook if consumers import hubEmitter
export const disposeHubEmitter = () => hubEmitter.dispose?.();
